import { type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';

import {
  bulkGetVocabularyTerms,
  type VocabTerm,
  type VocabularyDisplayDto,
  type VocabularyDetailFieldDto,
} from '@/api/vocabularies';
import { applyLinkTemplate, resolveFieldValues, termSubtitle, termTitle } from './term-display';

/** Title-case an unknown role key as a label fallback (`previousSymbols` -> `Previous Symbols`). */
function titleCase(role: string): string {
  const spaced = role.replaceAll(/([a-z])([A-Z])/g, '$1 $2').replaceAll(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Hydrates the term ids referenced by `resolve: "name"` detail fields to their names,
 * one bulk-get per distinct vocabulary. Returns an id -> name lookup (best-effort: missing
 * ids fall back to the raw id at the call site).
 */
function useResolvedNames(
  fields: readonly VocabularyDetailFieldDto[],
  term: VocabTerm,
): Record<string, string> {
  // vocabulary -> set of ids that need resolving
  const byVocab = new Map<string, Set<string>>();
  for (const field of fields) {
    if (field.resolve !== 'name') continue;
    const vocab = field.vocabulary;
    if (vocab == null || vocab === '') continue;
    const ids = resolveFieldValues(term, field.key);
    if (ids.length === 0) continue;
    const set = byVocab.get(vocab) ?? new Set<string>();
    ids.forEach((id) => set.add(id));
    byVocab.set(vocab, set);
  }

  const groups = [...byVocab.entries()].map(([vocab, ids]) => ({
    vocab,
    ids: [...ids].sort(),
  }));

  const results = useQueries({
    queries: groups.map((g) => ({
      queryKey: ['vocabulary', g.vocab, 'bulk-get', g.ids] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        bulkGetVocabularyTerms(g.vocab, g.ids, signal),
      staleTime: 5 * 60_000,
    })),
  });

  const lookup: Record<string, string> = {};
  for (const result of results) {
    for (const t of result.data ?? []) {
      if (t.name != null && t.name !== '') lookup[t.id] = t.name;
    }
  }
  return lookup;
}

interface VocabTermInfoContentProps {
  readonly term: VocabTerm;
  readonly display?: VocabularyDisplayDto | null;
  /** Source vocabulary name + version for the reference header (optional). */
  readonly vocabularyName?: string | null;
  readonly version?: string | null;
  /**
   * Cap the values rendered per multi-value field (e.g. associated genes),
   * trailing a "+N more" hint. Some HPO category terms carry ~100 genes, which
   * balloons the panel — the browse popover passes a small limit. Unset =
   * render all (the default, so the patient-page tooltip is unchanged).
   */
  readonly maxValuesPerField?: number;
}

/**
 * The body of the term info panel — mirrors the legacy PhenoTips `xTooltip`: a reference
 * header (source + version, linking out), the id + title, then one labelled row per
 * configured detail field. Symptom / parent ids are resolved to names. Rendered inside a
 * popover or a dialog by the caller.
 */
export function VocabTermInfoContent({
  term,
  display,
  vocabularyName,
  version,
  maxValuesPerField,
}: VocabTermInfoContentProps): ReactElement {
  const { t } = useTranslation();
  const fields = display?.detailFields ?? [];
  const names = useResolvedNames(fields, term);

  const title = termTitle(term, display);
  const subtitle = termSubtitle(term, display);
  const externalHref = applyLinkTemplate(display?.externalLink, { id: term.id });

  const roleLabel = (role: string): string =>
    t(`vocab.term.role.${role}`, { defaultValue: titleCase(role) });

  return (
    <div className="space-y-2.5 text-sm">
      {vocabularyName != null && vocabularyName !== '' && (
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {vocabularyName}
          {version != null && version !== '' && version !== 'unknown' ? ` · ${version}` : ''}
        </p>
      )}

      <div className="flex items-baseline gap-2">
        <span className="font-mono text-xs text-muted-foreground">{term.id}</span>
        <span className="font-semibold text-foreground">{title}</span>
      </div>
      {subtitle !== null && <p className="text-muted-foreground">{subtitle}</p>}

      <dl className="space-y-1.5">
        {fields.map((field) => {
          const raw = resolveFieldValues(term, field.key);
          if (raw.length === 0) return null;
          const values = field.resolve === 'name' ? raw.map((id) => names[id] ?? id) : raw;

          if (field.role === 'definition') {
            return (
              <p key={field.key} className="text-foreground">
                {values.join(' ')}
              </p>
            );
          }

          return (
            <div key={field.key} className="grid grid-cols-[max-content_1fr] gap-x-2">
              <dt className="text-xs font-medium text-muted-foreground">{roleLabel(field.role)}</dt>
              <dd className="text-foreground">
                <FieldValues
                  values={values}
                  rawValues={raw}
                  link={field.link ?? null}
                  max={maxValuesPerField}
                />
              </dd>
            </div>
          );
        })}
      </dl>

      {externalHref !== null && (
        <a
          href={externalHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          {t('vocab.term.viewSource')}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      )}
    </div>
  );
}

/**
 * Comma-separated values; each is a link when the field defines a per-value
 * `link` template. When `max` is set and exceeded, only the first `max` show,
 * followed by a muted "+N more" hint.
 */
function FieldValues({
  values,
  rawValues,
  link,
  max,
}: {
  readonly values: readonly string[];
  readonly rawValues: readonly string[];
  readonly link?: string | null;
  readonly max?: number | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const overflow =
    max !== undefined && max > 0 && values.length > max ? values.length - max : 0;
  const shown = overflow > 0 ? values.slice(0, max) : values;
  const more =
    overflow > 0 ? (
      <span className="text-muted-foreground"> {t('vocab.term.moreValues', { count: overflow })}</span>
    ) : null;

  if (link == null || link === '') {
    return (
      <span>
        {shown.join(', ')}
        {more}
      </span>
    );
  }
  const items: ReactNode[] = [];
  shown.forEach((value, i) => {
    // Link target is built from the raw value (the id/code), label is the (possibly resolved) value.
    const href = applyLinkTemplate(link, { value: rawValues[i] ?? value });
    if (i > 0) items.push(<span key={`sep-${String(i)}`}>, </span>);
    items.push(
      href !== null ? (
        <a
          key={`v-${String(i)}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {value}
        </a>
      ) : (
        <span key={`v-${String(i)}`}>{value}</span>
      ),
    );
  });
  return (
    <span>
      {items}
      {more}
    </span>
  );
}
